// A/B-Variante B fuer die Meta-Anzeige. Kontrolle A bleibt /lp/flex4success.
// Free-Registrierung wie Startseite: Free-KlickTipp-DOI (/auth/doi/request), Bestaetigungsseite, Cockpit erst nach DOI-Klick.
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Inbox, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PocketCoachDemo } from "@/components/formel/PocketCoachDemo";

// Unverlinkte Testseite: nicht indexieren.
const useNoIndex = () => useEffect(() => {
  const m = document.createElement("meta");
  m.name = "robots";
  m.content = "noindex, nofollow";
  document.head.appendChild(m);
  return () => m.remove();
}, []);

const ATTR_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"];
const STEPS = ["Situation schildern", "Passenden Impuls erhalten", "Möglichen ersten Satz finden"];
const INPUT = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-base text-slate-900 shadow-sm focus:border-[var(--fts-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--fts-accent)]/20";

const readAttribution = () => {
  const p = new URLSearchParams(window.location.search);
  return Object.fromEntries(ATTR_KEYS.filter((k) => p.get(k)).map((k) => [k, p.get(k)]));
};

const BRIDGE = [
  "In vielen Situationen spürst du den Unterschied sofort:",
  "Die eine Person hält sich genau an die Vorgabe. Die andere macht es auf ihre Art.",
  "Das kann nerven, oder?",
  "Warum ist das so? Für dich ist doch eigentlich klar, wie man es macht.",
  "Oder doch nicht?",
];
// Bis zur Klaerung durch Andreas: Bestaetigungs-Link statt "Magic-Link" (siehe Rueckfrage).
const LINK_WORD = "Bestätigungs-Link";

const PC_CFG = {
  source: "lp_pocketcoach", quelle: "lp-pocketcoach",
  headline: "Eigentlich alles klar, oder?", subheadline: "Warum läuft es dann doch anders?", bridge: BRIDGE,
};
// Variante zur Anzeige „Abgesprochen“: nur Headline + Bridge anders, Ablauf identisch.
const AB_CFG = {
  source: "lp_abgesprochen", quelle: "lp-abgesprochen",
  headline: "Wir hatten das doch anders besprochen.", subheadline: "Warum läuft’s dann wieder anders?",
  bridge: [
    "Du hast die Absprache noch klar im Kopf. Trotzdem macht dein Gegenüber es anders. Dann fragst du dich: Habe ich mich unklar ausgedrückt?",
    "Oder haben wir einfach Unterschiedliches verstanden?",
  ],
};

const Hook = ({ cfg }) => (
  <div className="text-center">
    <p className="inline-flex rounded-full bg-white border border-amber-300/70 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700" data-testid="lpb-tagline">PocketCoach</p>
    <h1 className="mt-3 font-extrabold tracking-tight text-slate-900" data-testid="lpb-headline">
      <span className="block text-4xl lg:text-[2.6rem] leading-[1.1]">{cfg.headline}</span>
      <span className="mt-1.5 block text-2xl lg:text-[1.75rem] leading-tight font-bold text-slate-800" data-testid="lpb-subheadline">{cfg.subheadline}</span>
    </h1>
    <div className="mt-3 mx-auto space-y-0.5 text-base leading-snug text-slate-700 max-w-3xl" data-testid="lpb-hook-bridge">
      {cfg.bridge.map((t, i) => (
        <p key={t} className={i === cfg.bridge.length - 1 ? "font-semibold text-slate-900" : ""}>{t}</p>
      ))}
    </div>
  </div>
);

const Loesung = () => (
  <div className="max-w-3xl" data-testid="lpb-solution">
    <p className="text-lg leading-relaxed text-slate-700" data-testid="lpb-bridge">
      Genau hier setzt der PocketCoach an: Du schilderst die Situation, bekommst einen passenden Impuls und einen möglichen ersten Satz für dein nächstes Gespräch.
    </p>
    <ol className="mt-6 space-y-2" data-testid="lpb-steps">
      {STEPS.map((t, i) => (
        <li key={t} className="flex items-center gap-3 text-base font-semibold text-slate-800">
          <span className="w-7 h-7 shrink-0 rounded-full bg-[var(--fts-accent)] text-white text-sm flex items-center justify-center">{i + 1}</span>{t}
        </li>
      ))}
    </ol>
  </div>
);

const OptInIntro = () => (
  <div className="mb-2.5 text-center" data-testid="lpb-optin-intro">
    <h2 className="text-xl font-extrabold leading-tight text-slate-900" data-testid="lpb-optin-headline">
      Finde heraus, welche Muster bei solchen Gesprächen eine Rolle spielen.
    </h2>
    <p className="mt-2 text-sm leading-relaxed text-slate-700" data-testid="lpb-optin-text">
      Gib deinen Vornamen und deine E-Mail-Adresse ein. Wir schicken dir einen {LINK_WORD} per E-Mail. Damit öffnest du deinen persönlichen PocketCoach.
    </p>
    <p className="mt-1 text-sm font-semibold text-slate-900">Du wirst Augen machen.</p>
  </div>
);

const OptInForm = ({ onSubmit, state }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ firstName, email, consent }); }} className="space-y-2.5" data-testid="lpb-form">
      <div className="grid gap-2.5">
      <div>
        <label htmlFor="lpb-fn" className="block text-sm font-semibold text-slate-900">Dein Vorname</label>
        <input id="lpb-fn" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={80} autoComplete="given-name" placeholder="Vorname" className={INPUT} data-testid="lpb-firstname-input" />
      </div>
      <div>
        <label htmlFor="lpb-mail" className="block text-sm font-semibold text-slate-900">Deine E-Mail-Adresse</label>
        <input id="lpb-mail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" placeholder="du@beispiel.de" className={INPUT} data-testid="lpb-email-input" />
      </div>
      </div>
      <label className="flex items-start gap-3 text-xs leading-relaxed text-slate-600">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300" data-testid="lpb-consent-checkbox" />
        <span>
          Ich möchte Flex4Success nutzen und bin einverstanden, dass Andreas mir dazu gelegentlich schreibt. Hinweise zum Umgang mit deinen Daten findest du in der{" "}
          <Link to="/datenschutz" className="underline underline-offset-2" data-testid="lpb-privacy-link">Datenschutzerklärung</Link>.
        </span>
      </label>
      {state.error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="lpb-error">{state.error}</div>}
      <button type="submit" disabled={state.submitting} data-testid="lpb-submit-button"
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--fts-accent)] px-6 py-3.5 text-lg font-bold text-white shadow-lg transition-opacity hover:opacity-95 disabled:opacity-60">
        {state.submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Einen Moment …</> : <>PocketCoach öffnen <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></>}
      </button>
      <p className="text-center text-xs text-slate-500" data-testid="lpb-facts">Kostenlos. Der {LINK_WORD} kommt per E-Mail.</p>
    </form>
  );
};

export default function LPPocketCoach() {
  return <OptInPage cfg={PC_CFG} />;
}

export function LPAbgesprochen() {
  return <OptInPage cfg={AB_CFG} />;
}

function OptInPage({ cfg }) {
  const navigate = useNavigate();
  const attribution = useRef(typeof window !== "undefined" ? readAttribution() : {});
  const [state, setState] = useState({ submitting: false, error: "" });

  useNoIndex();
  useEffect(() => { document.title = "PocketCoach · Flex4Success"; }, []);

  async function handleSubmit({ firstName, email, consent }) {
    const mail = email.trim().toLowerCase();
    if (!mail.includes("@")) return setState((s) => ({ ...s, error: "Bitte eine gültige Mail-Adresse eingeben." }));
    if (!consent) return setState((s) => ({ ...s, error: "Bitte Einverständnis bestätigen." }));
    setState({ submitting: true, error: "" });
    try {
      await api.post("/auth/doi/request", {
        email: mail, first_name: firstName.trim() || mail.split("@")[0], dsgvo: true,
        quelle: cfg.quelle, source: cfg.source, attribution: attribution.current,
      });
      navigate(`/lp/pocketcoach/danke?email=${encodeURIComponent(mail)}`);
    } catch (_e) {
      setState({ submitting: false, error: "Da ist etwas schiefgegangen. Bitte noch einmal versuchen." });
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="lp-pocketcoach-page">
      <div className="mx-auto max-w-5xl px-5 pt-6 pb-14 lg:pt-4">
        <Hook cfg={cfg} />
        <div className="mt-6 lg:mt-4 grid md:grid-cols-[0.8fr_1.2fr] gap-8 md:gap-10 items-start">
          <div className="md:order-2 rounded-2xl bg-white border border-slate-200 shadow-sm p-5" data-testid="lpb-optin-card">
            <OptInIntro />
            <OptInForm onSubmit={handleSubmit} state={state} />
          </div>
          <div className="md:order-1" data-testid="lpb-visual"><PocketCoachDemo /></div>
        </div>
        <div className="mt-14 border-t border-amber-200/70 pt-10"><Loesung /></div>
      </div>
    </div>
  );
}

export function LPPocketCoachDanke() {
  const [params] = useSearchParams();
  const email = (params.get("email") || "").trim();
  useNoIndex();
  useEffect(() => { document.title = "Bestätige deine E-Mail · Flex4Success"; }, []);
  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-4 py-12" data-testid="lp-pocketcoach-danke-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center">
        <div className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h1 className="text-2xl font-extrabold text-slate-900" data-testid="lpb-danke-headline">Bestätige deine E-Mail</h1>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid="lpb-danke-text">
          Wir haben dir eine E-Mail{email && <> an <span className="font-semibold text-slate-900" data-testid="lpb-danke-email">{email}</span></>} geschickt.
          Klick auf den Bestätigungs-Link, danach geht’s in deinen PocketCoach.
        </p>
        <div className="mt-6 rounded-xl bg-slate-50 border border-slate-100 p-4 text-left flex items-start gap-3" data-testid="lpb-danke-spam-hint">
          <Inbox className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-500 leading-relaxed">
            <span className="block text-sm font-semibold text-slate-800">Nichts im Postfach?</span>
            Schau auch im Spam- oder Werbung-Ordner nach. Manche Mail-Anbieter brauchen eine Minute.
          </p>
        </div>
      </div>
    </div>
  );
}
